import * as React from 'react';

import { BaseControlProps } from '../../models/BaseControlProps';
import { Message } from '../../models/Message';
import { CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, ImageList, ImageListItem, styled, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MessageAPIService } from '../../services/MessageAPIService';
import { useMessage } from '../Common/MessageContext/MessageContext';
import { useAuth } from '../../services/AuthContext';
import { UtilService } from '../../services/UtilService';
import { MessageAttachmentDetail } from '../../models/MessageAttachmentDetail';
import { AttachFile, Description, PictureAsPdf } from '@mui/icons-material';


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
      padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
  },
}));

interface MessageDetailProps extends BaseControlProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    message: Message;
    markAsRead: boolean;
    messageCount?: number;
    setMessageCount?: (number: number) => void;
}

export default function MessageDetail(props: MessageDetailProps) {
  const messageApiService = new MessageAPIService();
  const authContext = useAuth();
  const { showMessage } = useMessage();
  const [attachmentDetails, setAttachmentDetails] = React.useState<MessageAttachmentDetail[] | []>([]);
  const [loading, isLoading] = React.useState<boolean>(false);
  
  const getMessageAttachmentDetails  = async (messageId: number, attachmentId: number): Promise<MessageAttachmentDetail | boolean> => {
      const request = {
          portalMessageId: messageId,
          portalMessageAttachmentId: attachmentId
      }
      const response = await messageApiService.getMessageAttachment(request, authContext.token);
      
      if (response.error) {
          if (props.onMessageNeeded) {
              props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
            }
            showMessage(response.error, 'error');
      }
      else if (response.response) {
            return response.response;
      }
    
      return false;
  }

  const downloadAttachment = (attachmentDetails: MessageAttachmentDetail) => {
    const link = document.createElement('a');

    link.href = `data:${attachmentDetails.fileExtension};base64,${attachmentDetails.document}`;
    link.target = '_blank';
    link.download = attachmentDetails.fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  }

  const markMessageAsRead = async (messageId: number) => {
      const request = {
          userId: Number(authContext.userId),
          messageIds: messageId.toString()
      };
      
      messageApiService.markMessagesAsRead(request, authContext.token).then((response) => {
          if (response.error) {
              if (props.onMessageNeeded) {
                  props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
              }
              showMessage(response.error, 'error');
          }
          else {
              if (props.setMessageCount && props.messageCount && props.messageCount > 0 ) {
                  props.setMessageCount(props.messageCount - 1);
              }
          }
      });
  }

  React.useEffect(() => {

    setAttachmentDetails([]);

    if (props.markAsRead) {
        markMessageAsRead(props.message.portalMessageId);
    }

    const newAttachments: MessageAttachmentDetail[] = [];

    const loadAttachments = async () => {
      isLoading(true);  
      for (const attachment of props.message.attachments) {
        const result = await getMessageAttachmentDetails(props.message.portalMessageId, attachment.portalMessageAttachmentId);
        if (result !== false && typeof result === 'object' && 'portalMessageAttachmentId' in result) {
            newAttachments.push(result);
        }

        setAttachmentDetails(newAttachments);
      };
      isLoading(false);
      
    }

    loadAttachments();
  }, [props.message]);

  function getAttachmentIcon(attachment: MessageAttachmentDetail) {

      switch (attachment.fileExtension) {
          case 'png':
          case 'jpg':
          case 'jpeg':
          case 'gif':
              return (
                <img
                    srcSet={`data:image/${attachment.fileExtension};base64,${attachment.document}`}
                    src={`data:image/${attachment.fileExtension};base64,${attachment.document}`}
                    alt="message-images"
                    loading="lazy"
                />
              );
          case 'pdf':
              return <PictureAsPdf style={{ color: 'red', fontSize: '100px' }} />;
          case 'doc':
          case 'docx':
              return <Description style={{ color: 'blue', fontSize: '100px' }} />;
          default:
              return <AttachFile style={{ fontSize: '100px' }} />;
      }
  }

  const handleClose = () => {
      props.setVisible(false);
  }

  return (
    <>
      <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={props.visible}
            sx={{
              '& .MuiPaper-root': {
                  width: '600px', 
                  borderRadius: '6px',  
                  padding: '16px'
              },
            }}
        >
            <DialogTitle 
              sx={{ 
                m: 0, 
                p: 2,
                fontSize: '1.25rem',
                fontWeight: '600',
                color: 'text.primary',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between' 
              }} 
              id="customized-dialog-title" 
            >
            Messsage Detail
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[600],
                    '&:hover': {
                        color: (theme) => theme.palette.grey[800],
                    },
              }}
            >
            <CloseIcon />
            </IconButton>
            <DialogContent 
              sx={{
                padding: '24px',
                backgroundColor: 'white',
                overflowY: 'auto',
                maxHeight: '60vh',
              }}
              dividers
            >
              <Typography 
                gutterBottom
                sx={{
                  fontSize: '1rem',
                  lineHeight: '1.5',
                  color: 'text.secondary',
                  marginBottom: '16px',
                }}
              >
                  <div dangerouslySetInnerHTML={{ __html: props.message ? props.message.messageContent : ''}} />
              </Typography>

              <Divider sx={{ marginBottom: '16px' }} />

              {loading && ( <CircularProgress aria-label="Loading..." /> )}

              {props.message && attachmentDetails &&  (
                <ImageList sx={{ width: '100%', height: 'auto', overflow: 'hidden' }} cols={2} rowHeight={200}
                >
                  {attachmentDetails.map((attachment, index) => (
                      <ImageListItem 
                        key={attachment.portalMessageAttachmentId} 
                        onClick={() => downloadAttachment(attachment)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          transition: '0.3s',
                          '&:hover': { transform: 'scale(1.05)' }
                        }}
                      >
                          {getAttachmentIcon(attachment)}
                      </ImageListItem>
                  ))}
                </ImageList>
              )}

            </DialogContent>
        </BootstrapDialog>
    </>
  );
}